import {
  consumptionMethods, events as eventsStore, habits, metaAll, planStates, plans,
  purchaseTemplates, rescueStats, settings as settingsStore, STORE_NAMES,
} from './db.js';
import { estimateStorage, isPersisted } from './persistence.js';
import { nowIso } from '../core/time.js';

/**
 * Diagnostic bundle intended for bug reports. Strips free-text content
 * (motivation anchors, reflection notes, private labels) so the user can
 * share counts and structure without leaking sensitive habit details.
 */
export interface Diagnostics {
  app: 'wean';
  kind: 'diagnostics';
  generatedAt: string;
  appVersion?: string | undefined;
  schemaVersion?: number | undefined;
  installIdHash: string;
  persistedStorage: boolean;
  storage: { usage?: number | undefined; quota?: number | undefined };
  counts: Record<string, number>;
  eventsByType: Record<string, number>;
  habits: Array<{ id: string; category: string; domain?: string | undefined; createdAt: string; active: boolean }>;
  plans: Array<{ id: string; planType: string; pace: string; slipStyle: string; active: boolean }>;
  planStates: Array<{ planId: string; planType: string; lastExplanationLength: number }>;
  settings: { tone: string; theme: string; reducedMotion: string; notificationsEnabled: boolean; preferredRescueTypes: string[] };
  rescueStats: Array<{ moduleId: string; kind: string; starts: number; completions: number; abandonments: number; averageDurationMs: number }>;
  methods: Array<{ id: string; habitId: string; domain: string; kind: string; logMode: string; active: boolean }>;
  purchaseTemplates: Array<{ id: string; habitId: string; methodId?: string | undefined; mode: string; confidence: string; active: boolean }>;
  integrity: {
    habitsMissingDomain: number;
    activeHabitsMissingMethod: number;
    methodsOrphaned: number;
    purchaseTemplatesOrphanedHabit: number;
    purchaseTemplatesOrphanedMethod: number;
    invalidPurchaseEvents: number;
  };
}

async function sha256Short(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest('SHA-256', data as BufferSource);
  const bytes = new Uint8Array(buf);
  let hex = '';
  for (let i = 0; i < 8; i++) hex += (bytes[i] ?? 0).toString(16).padStart(2, '0');
  return hex;
}

export async function buildDiagnostics(): Promise<Diagnostics> {
  const meta = await metaAll();
  const allHabits = await habits.all();
  const allPlans = await plans.all();
  const allStates = await planStates.all();
  const allEvents = await eventsStore.all();
  const allRescue = await rescueStats.all();
  const allMethods = await consumptionMethods.all();
  const allTemplates = await purchaseTemplates.all();
  const s = await settingsStore.get();
  const installIdHash = await sha256Short(meta.install_id ?? 'unknown');

  const counts: Record<string, number> = {};
  for (const name of Object.values(STORE_NAMES)) counts[name] = 0;
  counts[STORE_NAMES.habits] = allHabits.length;
  counts[STORE_NAMES.plans] = allPlans.length;
  counts[STORE_NAMES.plan_states] = allStates.length;
  counts[STORE_NAMES.events] = allEvents.length;
  counts[STORE_NAMES.rescue_module_stats] = allRescue.length;
  counts[STORE_NAMES.consumption_methods] = allMethods.length;
  counts[STORE_NAMES.purchase_templates] = allTemplates.length;

  const eventsByType: Record<string, number> = {};
  for (const e of allEvents) eventsByType[e.type] = (eventsByType[e.type] ?? 0) + 1;

  const habitIds = new Set(allHabits.map((h) => h.id));
  const methodIds = new Set(allMethods.map((m) => m.id));
  const methodsByHabit = new Map<string, number>();
  for (const m of allMethods) methodsByHabit.set(m.habitId, (methodsByHabit.get(m.habitId) ?? 0) + 1);

  const integrity = {
    habitsMissingDomain: allHabits.filter((h) => !h.domain).length,
    activeHabitsMissingMethod: allHabits.filter((h) => h.active && (methodsByHabit.get(h.id) ?? 0) === 0).length,
    methodsOrphaned: allMethods.filter((m) => !habitIds.has(m.habitId)).length,
    purchaseTemplatesOrphanedHabit: allTemplates.filter((t) => !habitIds.has(t.habitId)).length,
    purchaseTemplatesOrphanedMethod: allTemplates.filter((t) => t.methodId !== undefined && !methodIds.has(t.methodId)).length,
    invalidPurchaseEvents: allEvents.filter((e) => {
      if (e.type !== 'purchase_logged') return false;
      const p = e.payload as { amount?: unknown; currency?: unknown; purchaseId?: unknown };
      return typeof p.purchaseId !== 'string'
        || typeof p.amount !== 'number' || !isFinite(p.amount)
        || typeof p.currency !== 'string' || p.currency.length === 0;
    }).length,
  };

  return {
    app: 'wean',
    kind: 'diagnostics',
    generatedAt: nowIso(),
    appVersion: meta.app_version,
    schemaVersion: meta.schema_version,
    installIdHash,
    persistedStorage: await isPersisted(),
    storage: await estimateStorage(),
    counts,
    eventsByType,
    habits: allHabits.map((h) => {
      const out: { id: string; category: string; domain?: string | undefined; createdAt: string; active: boolean } = {
        id: h.id, category: h.category, createdAt: h.createdAt, active: h.active,
      };
      if (h.domain !== undefined) out.domain = h.domain;
      return out;
    }),
    plans: allPlans.map((p) => ({ id: p.id, planType: p.planType, pace: p.pace, slipStyle: p.slipStyle, active: p.active })),
    planStates: allStates.map((ps) => ({
      planId: ps.planId,
      planType: ps.state.planType,
      lastExplanationLength: ps.state.lastExplanation?.length ?? 0,
    })),
    settings: {
      tone: s.tone, theme: s.theme, reducedMotion: s.reducedMotion,
      notificationsEnabled: s.notificationsEnabled, preferredRescueTypes: s.preferredRescueTypes,
    },
    rescueStats: allRescue.map((r) => ({
      moduleId: r.moduleId, kind: r.kind,
      starts: r.starts, completions: r.completions, abandonments: r.abandonments,
      averageDurationMs: r.averageDurationMs,
    })),
    methods: allMethods.map((m) => ({
      id: m.id, habitId: m.habitId, domain: m.domain, kind: m.kind,
      logMode: m.defaultLogMode, active: m.active,
    })),
    purchaseTemplates: allTemplates.map((t) => {
      const out: { id: string; habitId: string; methodId?: string | undefined; mode: string; confidence: string; active: boolean } = {
        id: t.id, habitId: t.habitId, mode: t.savingsMode, confidence: t.confidence, active: t.active,
      };
      if (t.methodId !== undefined) out.methodId = t.methodId;
      return out;
    }),
    integrity,
  };
}

export async function downloadDiagnostics(): Promise<void> {
  const d = await buildDiagnostics();
  const blob = new Blob([JSON.stringify(d, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  a.href = url; a.download = `wean-diagnostics-${ts}.json`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
